import { useContext, useEffect, useRef, useState } from 'react';
import {
	ArrowDown,
	ArrowUp,
	CheckCircle,
	ExternalLink,
	Image as ImageIcon,
	Loader,
	Megaphone,
	Pencil,
	Plus,
	Save,
	Trash2,
	Upload,
	XCircle
} from 'lucide-react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Dialog } from '../../components/Dialog';
import { EmptyState } from '../../components/EmptyState';
import Input from '../../components/Input';
import { ContainerSpinner } from '../../components/Spinner';
import { StoreAnnouncements } from '../../components/StoreAnnouncements';
import Textarea from '../../components/Textarea';
import { GlobalContext, type CountrySettings, type WebBanner } from '../../context/GlobalContext';
import Firebase from '../../firebase_setup';
import firestore from '../../firestore';
import { Button } from '../../shadcn/Button';
import { Switch } from '../../shadcn/Switch';
import { COUNTRIES, COUNTRY_LABELS } from '../../utils/constants';

type EditableWebBanner = WebBanner & { id: string };

const PRIMARY_URL_DESCRIPTION_LIMIT = 120;
const createBannerId = () => `banner-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const isValidHttpUrl = (value: string) => {
	try {
		return ['http:', 'https:'].includes(new URL(value).protocol);
	} catch {
		return false;
	}
};

const cleanBanner = (banner: EditableWebBanner): EditableWebBanner => {
	const title = banner.title?.trim();
	const description = banner.description?.trim();
	const imageUrl = banner.image_url?.trim();
	const redirectUrl = banner.redirect_url?.trim();
	const buttonText = banner.button_text?.trim();

	return {
		id: banner.id,
		...(title && { title }),
		...(description && { description }),
		...(imageUrl && { image_url: imageUrl }),
		...(redirectUrl && { redirect_url: redirectUrl }),
		...(redirectUrl && buttonText && { button_text: buttonText }),
		...(redirectUrl && banner.is_url_primary && { is_url_primary: true }),
		is_active: banner.is_active !== false
	};
};

function EditAnnouncementDialog({
	banner,
	onClose,
	onSave,
	saving,
	isNew
}: {
	banner: EditableWebBanner;
	onClose: () => void;
	onSave: (updatedBanner: EditableWebBanner) => void;
	saving: boolean;
	isNew: boolean;
}) {
	const [editedBanner, setEditedBanner] = useState<EditableWebBanner>({
		...banner
	});
	const [alertDialog, setAlertDialog] = useState<{
		title: string;
		message: string;
	} | null>(null);
	const [uploadingImage, setUploadingImage] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const isUrlPrimary = !!editedBanner.redirect_url?.trim() && editedBanner.is_url_primary === true;
	const descriptionLimit = isUrlPrimary ? PRIMARY_URL_DESCRIPTION_LIMIT : 300;

	const handleSave = () => {
		if (uploadingImage) {
			setAlertDialog({
				title: 'Imagen en proceso',
				message: 'Espera a que termine de subir la imagen.'
			});
			return;
		}
		if (!editedBanner.title?.trim() && !editedBanner.description?.trim()) {
			setAlertDialog({
				title: 'Contenido requerido',
				message: 'Debes proporcionar al menos un título o una descripción.'
			});
			return;
		}
		if (editedBanner.redirect_url?.trim() && !isValidHttpUrl(editedBanner.redirect_url.trim())) {
			setAlertDialog({
				title: 'Enlace no válido',
				message: 'Ingresa un enlace completo que comience con http:// o https://.'
			});
			return;
		}
		if (isUrlPrimary && (editedBanner.description?.length || 0) > PRIMARY_URL_DESCRIPTION_LIMIT) {
			setAlertDialog({
				title: 'Descripción demasiado larga',
				message: `Cuando el enlace es la acción principal, la descripción puede tener hasta ${PRIMARY_URL_DESCRIPTION_LIMIT} caracteres.`
			});
			return;
		}
		onSave(editedBanner);
	};

	const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		if (uploadingImage) return;

		const imageTypes = ['image/jpeg', 'image/png', 'image/webp'];
		const requiredAspectRatio = 1;
		const aspectRatioTolerance = 0.3;

		try {
			setUploadingImage(true);
			setUploadProgress(0);
			const file = event.target.files?.[0];
			if (!file) throw new Error('No file selected');

			if (!imageTypes.includes(file.type)) {
				setAlertDialog({
					title: 'Formato no compatible',
					message: 'Sube una imagen JPG, PNG o WebP.'
				});
				throw new Error('Invalid image type');
			}
			if (file.size > 5000000) {
				setAlertDialog({
					title: 'Imagen demasiado grande',
					message: 'La imagen no puede superar los 5 MB.'
				});
				throw new Error('File too large');
			}

			const validAspectRatio = await new Promise<boolean>((resolve) => {
				const image = new Image();
				const objectUrl = URL.createObjectURL(file);
				image.onload = () => {
					const aspectRatio = image.width / image.height;
					URL.revokeObjectURL(objectUrl);
					resolve(Math.abs(aspectRatio - requiredAspectRatio) <= aspectRatioTolerance);
				};
				image.onerror = () => {
					URL.revokeObjectURL(objectUrl);
					resolve(false);
				};
				image.src = objectUrl;
			});

			if (!validAspectRatio) {
				setAlertDialog({
					title: 'Proporción no compatible',
					message: 'La imagen debe ser aproximadamente cuadrada (proporción 1:1).'
				});
				throw new Error('Invalid aspect ratio');
			}

			const fileRef = Firebase.storage().ref().child(`store_banners/${Date.now()}_${file.name}`);
			const uploadTask = fileRef.put(file, {
				cacheControl: 'public,max-age=31536000,immutable'
			});

			uploadTask.on(
				'state_changed',
				(snapshot) => setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
				(error) => {
					console.error('Upload error:', error);
					setAlertDialog({
						title: 'Error',
						message: 'No se pudo subir la imagen. Intenta nuevamente.'
					});
					setUploadingImage(false);
				},
				async () => {
					try {
						const downloadUrl = await uploadTask.snapshot.ref.getDownloadURL();
						setEditedBanner((current) => ({
							...current,
							image_url: downloadUrl
						}));
					} catch (error) {
						console.error('Download URL error:', error);
						setAlertDialog({
							title: 'Error',
							message: 'La imagen se subió, pero no se pudo obtener su enlace.'
						});
					} finally {
						setUploadingImage(false);
						setUploadProgress(0);
						event.target.value = '';
					}
				}
			);
		} catch (error) {
			console.error('Error uploading image:', error);
			setUploadingImage(false);
			setUploadProgress(0);
			event.target.value = '';
		}
	};

	return (
		<>
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable>
					<p className="text-neutral-200">{alertDialog.message}</p>
				</Dialog>
			)}
			<Dialog
				title={isNew ? 'Nuevo comunicado' : 'Editar comunicado'}
				onClose={onClose}
				closable={!saving}
				cerrarButton={false}
				className="!w-[min(720px,95%)] !p-3 md:!p-6"
			>
				<form
					className="space-y-6"
					onSubmit={(event) => {
						event.preventDefault();
						handleSave();
					}}
				>
					<div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-800 bg-neutral-925 p-4">
						<div>
							<p className="text-sm font-medium text-neutral-100">Mostrar en las tiendas</p>
							<p className="mt-0.5 text-xs text-neutral-400">Los comunicados inactivos permanecen guardados.</p>
						</div>
						<Switch
							checked={editedBanner.is_active !== false}
							onCheckedChange={(checked) =>
								setEditedBanner((current) => ({
									...current,
									is_active: checked
								}))
							}
							aria-label="Mostrar comunicado en las tiendas"
						/>
					</div>

					<div className="grid gap-4">
						<div>
							<Input
								floatingLabel="Título"
								placeholder="Ej: Horario especial"
								value={editedBanner.title || ''}
								maxLength={80}
								onChange={(value) => setEditedBanner((current) => ({ ...current, title: value }))}
							/>
							<p className="mt-1 text-right text-xs text-neutral-500">{editedBanner.title?.length || 0}/80</p>
						</div>
						<div>
							<Textarea
								floatingLabel="Descripción"
								placeholder="Escribe la información que deben ver las tiendas"
								value={editedBanner.description || ''}
								maxLength={descriptionLimit}
								rows={4}
								resize="vertical"
								onChange={(value) =>
									setEditedBanner((current) => ({
										...current,
										description: value
									}))
								}
								onKeyDown={(event) => event.stopPropagation()}
							/>
							<div className="mt-1 flex justify-between gap-3 text-xs text-neutral-500">
								<span>
									{isUrlPrimary
										? `Con enlace principal, usa una descripción breve de hasta ${PRIMARY_URL_DESCRIPTION_LIMIT} caracteres.`
										: 'Debes completar al menos uno de los dos campos.'}
								</span>
								<span
									className={(editedBanner.description?.length || 0) > descriptionLimit ? 'font-medium text-red-400' : undefined}
								>
									{editedBanner.description?.length || 0}/{descriptionLimit}
								</span>
							</div>
						</div>
					</div>

					<div className="space-y-3 border-t border-neutral-800 pt-5">
						<div>
							<p className="text-sm font-medium text-neutral-200">Acción opcional</p>
							<p className="mt-0.5 text-xs text-neutral-500">
								Agrega un enlace para mostrar un botón independiente del texto del comunicado.
							</p>
						</div>
						<Input
							type="url"
							floatingLabel="Enlace"
							placeholder="https://ejemplo.com/detalles"
							value={editedBanner.redirect_url || ''}
							maxLength={500}
							onChange={(value) =>
								setEditedBanner((current) => ({
									...current,
									redirect_url: value,
									...(!value.trim() && { is_url_primary: false })
								}))
							}
						/>
						{editedBanner.redirect_url?.trim() && (
							<>
								<div>
									<Input
										floatingLabel="Texto del botón"
										placeholder="Ver detalles"
										value={editedBanner.button_text || ''}
										maxLength={30}
										onChange={(value) => setEditedBanner((current) => ({ ...current, button_text: value }))}
									/>
									<div className="mt-1 flex justify-between gap-3 text-xs text-neutral-500">
										<span>Si lo dejas vacío, se usará “Ver detalles”.</span>
										<span>{editedBanner.button_text?.length || 0}/30</span>
									</div>
								</div>
								<div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-800 bg-neutral-925 p-4">
									<div>
										<p className="text-sm font-medium text-neutral-100">Usar enlace como acción principal</p>
										<p className="mt-0.5 text-xs text-neutral-400">
											Muestra el botón directamente y reduce la descripción a una línea.
										</p>
									</div>
									<Switch
										checked={editedBanner.is_url_primary === true}
										onCheckedChange={(checked) => setEditedBanner((current) => ({ ...current, is_url_primary: checked }))}
										aria-label="Usar enlace como acción principal"
									/>
								</div>
							</>
						)}
					</div>

					<div className="space-y-3 border-t border-neutral-800 pt-5">
						<div className="flex items-center justify-between gap-3">
							<p className="text-sm font-medium text-neutral-200">Imagen opcional</p>
							{editedBanner.image_url && !uploadingImage && (
								<button
									type="button"
									onClick={() =>
										setEditedBanner((current) => ({
											...current,
											image_url: undefined
										}))
									}
									className="text-xs font-medium text-red-400 transition-colors hover:text-red-300"
								>
									Eliminar imagen
								</button>
							)}
						</div>
						<input
							type="file"
							accept="image/jpeg,image/png,image/webp"
							onChange={handleImageUpload}
							disabled={uploadingImage || saving}
							className="hidden"
							id={`announcement-image-${banner.id}`}
						/>
						<label
							htmlFor={`announcement-image-${banner.id}`}
							className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-700 bg-neutral-925 p-5 transition-colors hover:border-primary-500 ${uploadingImage ? 'pointer-events-none opacity-60' : ''}`}
						>
							{uploadingImage ? (
								<>
									<Loader className="mb-2 size-7 animate-spin text-primary-500" />
									<p className="text-sm font-medium text-neutral-300">Subiendo imagen...</p>
									<div className="mt-3 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-neutral-800">
										<div className="h-full bg-primary-500" style={{ width: `${uploadProgress}%` }} />
									</div>
									<p className="mt-1 text-xs text-neutral-400">{Math.round(uploadProgress)}%</p>
								</>
							) : editedBanner.image_url ? (
								<>
									<img src={editedBanner.image_url} alt="" className="mb-3 size-20 rounded-lg object-cover" />
									<p className="text-sm font-medium text-neutral-300">Cambiar imagen</p>
								</>
							) : (
								<>
									<Upload className="mb-2 size-7 text-neutral-400" />
									<p className="text-sm font-medium text-neutral-300">Subir imagen</p>
									<p className="mt-1 text-xs text-neutral-500">JPG, PNG o WebP · máximo 5 MB · proporción 1:1</p>
								</>
							)}
						</label>
					</div>

					<div className="overflow-hidden rounded-lg border border-neutral-800">
						<div className="border-b border-neutral-800 bg-neutral-925 px-4 py-2 text-xs font-medium text-neutral-400">
							Vista previa
						</div>
						<StoreAnnouncements announcements={[editedBanner]} includeInactive autoRotate={false} />
					</div>

					<div className="flex justify-end gap-3 border-t border-neutral-800 pt-4">
						<Button type="button" variant="secondary" onClick={onClose} disabled={saving} className="!w-auto">
							Cancelar
						</Button>
						<Button type="submit" variant="primary" loading={saving} className="!w-auto">
							<Save className="size-4" />
							Guardar comunicado
						</Button>
					</div>
				</form>
			</Dialog>
		</>
	);
}

export default function StoreAnnouncementsAdmin() {
	const { internalCountry } = useContext(GlobalContext);
	const country = internalCountry?.get() || 'venezuela';
	const countryRef = useRef(country);
	countryRef.current = country;
	const [banners, setBanners] = useState<EditableWebBanner[]>([]);
	const [loadedCountry, setLoadedCountry] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editingBanner, setEditingBanner] = useState<EditableWebBanner | null>(null);
	const [isCreating, setIsCreating] = useState(false);
	const [deletingBanner, setDeletingBanner] = useState<EditableWebBanner | null>(null);
	const [alertDialog, setAlertDialog] = useState<{
		title: string;
		message: string;
	} | null>(null);

	useEffect(() => {
		setBanners([]);
		setLoadedCountry(null);
		setLoading(true);
		setLoadError(false);
		setEditingBanner(null);
		setIsCreating(false);
		setDeletingBanner(null);

		let active = true;
		const unsubscribe = firestore
			.collection('CountrySettings')
			.doc(country)
			.onSnapshot(
				(doc) => {
					if (!active) return;
					const settings = doc.data() as CountrySettings | undefined;
					const configuredBanners = settings?.web_banners;
					const source = configuredBanners ?? (settings?.web_banner ? [settings.web_banner] : []);
					setBanners(
						source.map((banner, index) => ({
							...banner,
							id: banner.id || `legacy-banner-${index}`
						}))
					);
					setLoadedCountry(country);
					setLoading(false);
				},
				(error) => {
					if (!active) return;
					console.error('Error loading announcements:', error);
					setLoadError(true);
					setLoading(false);
				}
			);

		return () => {
			active = false;
			unsubscribe();
		};
	}, [country]);

	const persistBanners = async (nextBanners: EditableWebBanner[]) => {
		if (loadedCountry !== country) return false;

		const targetCountry = country;
		setSaving(true);
		try {
			const cleanedBanners = nextBanners.map(cleanBanner);
			const legacyBanner = cleanedBanners.find((banner) => banner.is_active !== false) || { is_active: false };
			await firestore.collection('CountrySettings').doc(targetCountry).update({
				web_banners: cleanedBanners,
				web_banner: legacyBanner
			});
			if (countryRef.current === targetCountry) setBanners(cleanedBanners);
			return true;
		} catch (error) {
			console.error('Error saving announcements:', error);
			setAlertDialog({
				title: 'Error',
				message: 'No se pudieron guardar los comunicados. Intenta nuevamente.'
			});
			return false;
		} finally {
			setSaving(false);
		}
	};

	const handleSaveBanner = async (updatedBanner: EditableWebBanner) => {
		const exists = banners.some((banner) => banner.id === updatedBanner.id);
		const nextBanners = exists
			? banners.map((banner) => (banner.id === updatedBanner.id ? updatedBanner : banner))
			: [...banners, updatedBanner];
		if (await persistBanners(nextBanners)) {
			setEditingBanner(null);
			setIsCreating(false);
		}
	};

	const handleMoveBanner = async (index: number, direction: -1 | 1) => {
		const targetIndex = index + direction;
		if (targetIndex < 0 || targetIndex >= banners.length) return;
		const nextBanners = [...banners];
		[nextBanners[index], nextBanners[targetIndex]] = [nextBanners[targetIndex], nextBanners[index]];
		await persistBanners(nextBanners);
	};

	const handleDeleteBanner = async (): Promise<true | string> => {
		if (!deletingBanner) return true;
		const saved = await persistBanners(banners.filter((banner) => banner.id !== deletingBanner.id));
		if (saved) {
			setDeletingBanner(null);
			return true;
		}
		return 'No se pudo eliminar el comunicado.';
	};

	const activeBanners = banners.filter(
		(banner) => banner.is_active !== false && (banner.title?.trim() || banner.description?.trim())
	);

	if (loading) return <ContainerSpinner size={3} />;

	if (loadError) {
		return (
			<div className="mx-auto w-full max-w-7xl p-4 md:p-6">
				<div className="rounded-lg border border-neutral-800 bg-neutral-1000">
					<EmptyState
						icon={Megaphone}
						title="No se pudieron cargar los comunicados"
						subtitle="Recarga la página e intenta nuevamente."
					/>
				</div>
			</div>
		);
	}

	if (loadedCountry !== country) return <ContainerSpinner size={3} />;

	return (
		<div className="mx-auto w-full max-w-7xl space-y-6 p-4 md:p-6">
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable>
					<p className="text-neutral-200">{alertDialog.message}</p>
				</Dialog>
			)}
			{editingBanner && (
				<EditAnnouncementDialog
					banner={editingBanner}
					onClose={() => {
						setEditingBanner(null);
						setIsCreating(false);
					}}
					onSave={handleSaveBanner}
					saving={saving}
					isNew={isCreating}
				/>
			)}
			{deletingBanner && (
				<ConfirmDialog
					title="Eliminar comunicado"
					message={`¿Deseas eliminar “${deletingBanner.title || 'Sin título'}”? Esta acción no se puede deshacer.`}
					onConfirm={handleDeleteBanner}
					onClose={() => setDeletingBanner(null)}
				/>
			)}

			<div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
				<div>
					<h1 className="text-2xl font-bold text-neutral-100">Comunicados para tiendas</h1>
					<p className="mt-1 text-sm text-neutral-400">
						Administra los anuncios visibles en {COUNTRY_LABELS[country as (typeof COUNTRIES)[number]]}.
					</p>
				</div>
				<Button
					variant="primary"
					className="!w-auto self-start"
					onClick={() => {
						setIsCreating(true);
						setEditingBanner({ id: createBannerId(), is_active: true });
					}}
				>
					<Plus className="size-4" />
					Nuevo comunicado
				</Button>
			</div>

			<section className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-1000">
				<div className="flex items-center justify-between gap-4 border-b border-neutral-800 px-4 py-3 md:px-6">
					<div>
						<h2 className="font-semibold text-neutral-100">Vista previa en tienda</h2>
						<p className="mt-0.5 text-xs text-neutral-500">Los comunicados activos avanzan automáticamente cada 7 segundos.</p>
					</div>
					<span className="text-xs font-medium text-neutral-400">
						{activeBanners.length} activo{activeBanners.length === 1 ? '' : 's'}
					</span>
				</div>
				{activeBanners.length ? (
					<StoreAnnouncements announcements={activeBanners} />
				) : (
					<EmptyState
						icon={Megaphone}
						title="No hay comunicados activos"
						subtitle="Crea uno nuevo o activa un comunicado guardado."
					/>
				)}
			</section>

			<section className="space-y-3">
				<div className="flex items-end justify-between gap-4">
					<div>
						<h2 className="text-lg font-semibold text-neutral-100">Orden de publicación</h2>
						<p className="mt-0.5 text-xs text-neutral-500">El orden define la secuencia del carrusel.</p>
					</div>
					<span className="text-sm text-neutral-500">{banners.length} en total</span>
				</div>

				{banners.length === 0 ? (
					<div className="rounded-lg border border-neutral-800 bg-neutral-1000">
						<EmptyState icon={Megaphone} title="No hay comunicados" subtitle="Crea el primer comunicado para comenzar." />
					</div>
				) : (
					<div className="space-y-3">
						{banners.map((banner, index) => (
							<article
								key={banner.id}
								className="flex flex-col gap-4 rounded-lg border border-neutral-800 bg-neutral-1000 p-4 sm:flex-row sm:items-center"
							>
								<div className="flex min-w-0 flex-1 items-center gap-4">
									{banner.image_url ? (
										<img src={banner.image_url} alt="" className="size-14 flex-shrink-0 rounded-lg object-cover" />
									) : (
										<div className="flex size-14 flex-shrink-0 items-center justify-center rounded-lg bg-primary-500/10">
											<ImageIcon className="size-5 text-primary-500" />
										</div>
									)}
									<div className="min-w-0 flex-1">
										<div className="mb-1 flex flex-wrap items-center gap-2">
											<span className="text-xs font-medium text-neutral-500">#{index + 1}</span>
											{banner.is_active !== false ? (
												<span className="flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-400">
													<CheckCircle className="size-3" /> Activo
												</span>
											) : (
												<span className="flex items-center gap-1 rounded-full bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-400">
													<XCircle className="size-3" /> Inactivo
												</span>
											)}
											{banner.redirect_url && (
												<span className="flex items-center gap-1 rounded-full bg-primary-500/10 px-2 py-0.5 text-xs font-medium text-primary-400">
													<ExternalLink className="size-3" /> {banner.is_url_primary ? 'Enlace principal' : 'Enlace'}
												</span>
											)}
										</div>
										<h3 className="truncate text-sm font-semibold text-neutral-100">{banner.title || 'Sin título'}</h3>
										<p className="mt-1 line-clamp-2 text-xs leading-relaxed text-neutral-400">
											{banner.description || 'Sin descripción'}
										</p>
									</div>
								</div>

								<div className="flex items-center justify-end gap-1 border-t border-neutral-800 pt-3 sm:border-l sm:border-t-0 sm:pl-3 sm:pt-0">
									<button
										type="button"
										onClick={() => handleMoveBanner(index, -1)}
										disabled={saving || index === 0}
										className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-900 hover:text-neutral-100 disabled:opacity-25"
										title="Mover arriba"
									>
										<ArrowUp className="size-4" />
									</button>
									<button
										type="button"
										onClick={() => handleMoveBanner(index, 1)}
										disabled={saving || index === banners.length - 1}
										className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-900 hover:text-neutral-100 disabled:opacity-25"
										title="Mover abajo"
									>
										<ArrowDown className="size-4" />
									</button>
									<button
										type="button"
										onClick={() => {
											setIsCreating(false);
											setEditingBanner(banner);
										}}
										disabled={saving}
										className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-900 hover:text-neutral-100 disabled:opacity-25"
										title="Editar comunicado"
									>
										<Pencil className="size-4" />
									</button>
									<button
										type="button"
										onClick={() => setDeletingBanner(banner)}
										disabled={saving}
										className="rounded-lg p-2 text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300 disabled:opacity-25"
										title="Eliminar comunicado"
									>
										<Trash2 className="size-4" />
									</button>
								</div>
							</article>
						))}
					</div>
				)}
			</section>
		</div>
	);
}
